import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import TrackingScripts from './components/TrackingScripts';
import './App.css';
import './index.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import FreelancerDashboard from './pages/FreelancerDashboard';
import ClientDashboard from './pages/ClientDashboard';
import CorporateDashboard from './pages/CorporateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import GigMarketplace from './pages/GigMarketplace';
import ProjectMarketplace from './pages/ProjectMarketplace';
import GigDetail from './pages/GigDetail';
import ProjectDetail from './pages/ProjectDetail';
import FreelancerProfile from './pages/FreelancerProfile';
import MessagesPage from './pages/MessagesPage';
import ContractDetail from './pages/ContractDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import SubUsersPage from './pages/SubUsersPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PortfolioGallery from './pages/PortfolioGallery';
import ProfileEditPage from './pages/ProfileEditPage';
import NotificationsPage from './pages/NotificationsPage';
import WalletPage from './pages/WalletPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InvoicesPage from './pages/InvoicesPage';
import AdminLoginPage from './pages/AdminLoginPage';
import SubAdminLoginPage from './pages/SubAdminLoginPage';
import ExplorePage from './pages/ExplorePage';
import { ExploreGigDetail, ExploreProjectDetail, ExploreFreelancerDetail } from './pages/ExploreDetails';
import ContentPage from './pages/ContentPage';
import PublicProfile from './pages/PublicProfile';

// Subdomain helpers
const hostname = window.location.hostname;
const IS_ADMIN_PORTAL = hostname.startsWith('ctrlhitch.');
const IS_SUBADMIN_PORTAL = hostname.startsWith('subhitch.');

// Cleanup stale inline styles left by payment gateways (Razorpay)
const RouteCleanup = ({ children }) => {
  const location = useLocation();
  useEffect(() => {
    document.body.style.removeProperty('pointer-events');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('contain');
  }, [location.pathname]);
  return children;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    if (IS_ADMIN_PORTAL) return <Navigate to="/admin/login" />;
    if (IS_SUBADMIN_PORTAL) return <Navigate to="/subadmin/login" />;
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) {
    if (IS_ADMIN_PORTAL) return <Navigate to="/admin/login" />;
    if (IS_SUBADMIN_PORTAL) return <Navigate to="/subadmin/login" />;
    return <Navigate to="/login" />;
  }

  switch (user.role) {
    case 'freelancer':
      return <FreelancerDashboard />;
    case 'client':
      return <ClientDashboard />;
    case 'corporate':
      return <CorporateDashboard />;
    case 'admin':
      return <AdminDashboard />;
    case 'super_admin':
      return <AdminDashboard />;
    default:
      return <Navigate to="/" />;
  }
};

// Subdomain lock — on portal subdomains, only portal login + dashboard are accessible
const SubdomainWrapper = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (IS_ADMIN_PORTAL) {
      const allowed = ['/admin/login', '/dashboard'];
      if (!allowed.includes(location.pathname)) {
        navigate(user ? '/dashboard' : '/admin/login', { replace: true });
      }
    } else if (IS_SUBADMIN_PORTAL) {
      const allowed = ['/subadmin/login', '/dashboard'];
      if (!allowed.includes(location.pathname)) {
        navigate(user ? '/dashboard' : '/subadmin/login', { replace: true });
      }
    }
  }, [location.pathname, user, navigate]);

  return children;
};

function AppContent() {
  const { user } = useAuth();

  return (
    <div className="App">
      <BrowserRouter>
        <RouteCleanup>
        <SubdomainWrapper>
        <Routes>
          {/* ── Portal login routes ── */}
          <Route path="/admin/login" element={
            IS_ADMIN_PORTAL || !IS_SUBADMIN_PORTAL
              ? <AdminLoginPage />
              : <Navigate to="/subadmin/login" />
          } />
          <Route path="/subadmin/login" element={
            IS_SUBADMIN_PORTAL || !IS_ADMIN_PORTAL
              ? <SubAdminLoginPage />
              : <Navigate to="/admin/login" />
          } />

          {/* ── Regular routes (hidden on portal subdomains) ── */}
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : (IS_ADMIN_PORTAL ? <Navigate to="/admin/login" /> : IS_SUBADMIN_PORTAL ? <Navigate to="/subadmin/login" /> : <LandingPage />)} />
          <Route path="/login" element={IS_ADMIN_PORTAL ? <Navigate to="/admin/login" /> : IS_SUBADMIN_PORTAL ? <Navigate to="/subadmin/login" /> : user ? <Navigate to="/dashboard" /> : <LoginPage />} />
          <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Public routes (no auth) */}
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/explore/gig/:gigId" element={<ExploreGigDetail />} />
          <Route path="/explore/project/:projectId" element={<ExploreProjectDetail />} />
          <Route path="/explore/freelancer/:userId" element={<ExploreFreelancerDetail />} />
          <Route path="/page/:slug" element={<ContentPage />} />
          <Route path="/u/:userCode" element={<PublicProfile />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />

          <Route
            path="/gigs"
            element={
              <ProtectedRoute>
                <GigMarketplace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/gigs/:gigId"
            element={
              <ProtectedRoute>
                <GigDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectMarketplace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile/:userId"
            element={
              <ProtectedRoute>
                <FreelancerProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/:contractId"
            element={
              <ProtectedRoute>
                <ContractDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-cancel"
            element={
              <ProtectedRoute>
                <PaymentCancel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sub-users"
            element={
              <ProtectedRoute>
                <SubUsersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <PortfolioGallery />
              </ProtectedRoute>
            }
          />

          <Route
            path="/portfolio/:userId"
            element={
              <ProtectedRoute>
                <PortfolioGallery />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <ProfileEditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </SubdomainWrapper>
        </RouteCleanup>
      </BrowserRouter>
      <Toaster position="bottom-center" richColors closeButton />
      <TrackingScripts />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
